import { FC, ReactNode } from 'react'
import { Button, Modal } from 'shared'
import './AlertModal.scss'

type AlertType = 'error' | 'success' | 'warning' | 'info'

type Props = {
  open: boolean,
  type?: AlertType,
  /** Custom icon — overrides {@link type}'s default. */
  icon?: ReactNode,
  title: string,
  description?: ReactNode,
  /** Primary button label. (Legacy callers still pass `buttonText`.) */
  buttonLabel?: string,
  /** @deprecated use {@link buttonLabel}. */
  buttonText?: string,
  /** @deprecated use {@link onClose} or pass {@link buttonLabel} + onClose. */
  onButtonClick?: () => void,
  onClose?: () => void,
  /** Optional secondary action (e.g. "Resend Activation Link"). */
  secondaryButtonLabel?: string,
  onSecondaryAction?: () => void,
  secondaryLoading?: boolean
}

const TYPE_GLYPH: Record<AlertType, string> = {
  error: '!',
  success: '✓',
  warning: '!',
  info: 'i'
}

const AlertModal: FC<Props> = ({
  open,
  type,
  icon,
  title,
  description,
  buttonLabel,
  buttonText,
  onButtonClick,
  onClose,
  secondaryButtonLabel,
  onSecondaryAction,
  secondaryLoading
}) => {
  const renderIcon = () => {
    if (icon) return icon
    if (!type) return null
    // Map warning → error styling so the SCSS only needs three variants.
    const variant: 'error' | 'success' | 'info' =
      type === 'warning' ? 'error' : type === 'info' ? 'info' : type
    return (
      <div
        className={`alert-modal__icon alert-modal__icon--${variant}`}
        aria-hidden
      >
        {TYPE_GLYPH[type]}
      </div>
    )
  }

  const primaryLabel = buttonLabel ?? buttonText
  const handlePrimary = onButtonClick ?? onClose

  return (
    <Modal
      open={open}
      size="xs"
      className="alert-modal"
      onClose={onClose}
      body={
        <>
          {renderIcon()}
          <h3 className="alert-modal__title">{title}</h3>
          {description && (
            <p className="alert-modal__description">{description}</p>
          )}
          {primaryLabel && (
            <Button
              type="button"
              appearance="primary"
              onClick={handlePrimary}
              className="alert-modal__btn"
            >
              {primaryLabel}
            </Button>
          )}
          {secondaryButtonLabel && onSecondaryAction && (
            <button
              type="button"
              className="alert-modal__btn-secondary"
              onClick={onSecondaryAction}
              disabled={secondaryLoading}
            >
              {secondaryLoading ? 'Working…' : secondaryButtonLabel}
            </button>
          )}
        </>
      }
    />
  )
}
export default AlertModal
